import React, { useState } from 'react';
import { Table, Button, Space, Empty, Modal, Alert, message, Typography, Spin } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { ColumnType } from 'antd/es/table';
import { PersonalAccessToken } from '@/services/identity/pat.service';
import { UsePersonalAccessTokensResult } from '../viewmodels/personal-access-tokens.viewmodel';
import { logError } from '@/appInsights';

export interface PersonalAccessTokensTableProps {
  data: UsePersonalAccessTokensResult;
  onCreateClick: () => void;
}

export const PersonalAccessTokensTable: React.FC<PersonalAccessTokensTableProps> = ({
  data,
  onCreateClick,
}) => {
  const { t } = useTranslation();
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);

  const handleDelete = (tokenId: string) => {
    Modal.confirm({
      title: t('common.patDeleteConfirm') || 'Delete Token',
      content:
        t('common.patDeleteConfirmMessage') ||
        'Are you sure you want to delete this token? This action cannot be undone.',
      okText: t('common.delete') || 'Delete',
      okType: 'danger',
      cancelText: t('common.cancel') || 'Cancel',
      onOk: async () => {
        try {
          setDeleteLoading(tokenId);
          await data.delete(tokenId);
          message.success(t('common.patDeleteSuccess') || 'Token deleted successfully');
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to delete token';
          logError(errorMessage, { action: 'delete_token', tokenId });
          message.error(errorMessage);
        } finally {
          setDeleteLoading(null);
        }
      },
    });
  };

  const columns: ColumnType<PersonalAccessToken>[] = [
    {
      title: t('common.patTokenName') || 'Token Name',
      dataIndex: 'name',
      key: 'name',
      width: '20%',
    },
    {
      title: t('common.patScopes') || 'Scopes',
      dataIndex: 'scopes',
      key: 'scopes',
      width: '25%',
      render: (scopes: string[]) => scopes.join(', ') || '-',
    },
    {
      title: t('common.createdAt') || 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: '15%',
      render: (createdAt: string) =>
        createdAt ? dayjs(createdAt).format('DD/MM/YYYY HH:mm') : '-',
    },
    {
      title: t('common.patExpiresAt') || 'Expires At',
      dataIndex: 'expiresAt',
      key: 'expiresAt',
      width: '15%',
      render: (expiresAt: string) =>
        expiresAt ? dayjs(expiresAt).format('DD/MM/YYYY HH:mm') : '-',
    },
    {
      title: t('common.patLastUsedAt') || 'Last Used At',
      dataIndex: 'lastUsedAt',
      key: 'lastUsedAt',
      width: '15%',
      render: (lastUsedAt?: string) =>
        lastUsedAt ? dayjs(lastUsedAt).format('DD/MM/YYYY HH:mm') : 'Never',
    },
    {
      title: t('common.action') || 'Action',
      key: 'action',
      width: '10%',
      render: (_, record) => (
        <Space>
          <Button
            type='text'
            danger
            size='small'
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
            loading={deleteLoading === record.id}
          />
        </Space>
      ),
    },
  ];

  if (data.error) {
    return (
      <Alert message={t('common.error')} description={data.error.message} type='error' showIcon />
    );
  }

  if (data.loading && data.tokens.length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 0' }}>
        <Spin />
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          marginBottom: '24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <h3 style={{ marginTop: 0, marginBottom: 0, color: '#8c8c8c', fontWeight: 500 }}>
          {t('common.patPersonalAccessTokens') || 'Personal Access Tokens'}
        </h3>
        <Button type='primary' icon={<PlusOutlined />} onClick={onCreateClick}>
          {t('common.patCreateToken') || 'Create Token'}
        </Button>
      </div>

      {data.tokens.length === 0 ? (
        <Empty
          description={t('common.patNoTokens') || 'No personal access tokens'}
          style={{ paddingTop: '32px', paddingBottom: '32px' }}
        />
      ) : (
        <>
          <Table
            columns={columns}
            dataSource={data.tokens}
            rowKey='id'
            pagination={false}
            loading={data.loading}
            size='middle'
          />
          <div style={{ marginTop: '16px', textAlign: 'right' }}>
            <Typography.Text type='secondary'>
              Total: <strong>{data.tokens.length}</strong> token
              {data.tokens.length !== 1 ? 's' : ''}
            </Typography.Text>
          </div>
        </>
      )}
    </div>
  );
};
